import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bot,
  CalendarClock,
  ChevronDown,
  Filter,
  Loader2,
  Play,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { leadChat } from "../../services/collavueApi";
import {
  subscribeLeadCommands,
  createLeadCommand,
  updateLeadCommand,
  deleteLeadCommand,
  COLLAVUE_COSTS,
  COLLAVUE_MAX_DAILY_COMMANDS,
} from "../../services/collavueStore";
import { useToast } from "../../contexts/ToastProvider";

const EXAMPLES = [
  "Find every lead that mentions pricing",
  "Which leads are asking for recommendations?",
  "Summarize the pain points in my hot leads",
];

// Floating lead-assistant chat. Collapsed it's a single button in the bottom
// corner so it never competes with the inbox itself; opened it's a compact
// panel (full-screen sheet on mobile). Filter-type answers hand their matching
// lead IDs back to the inbox via onApplyFilter, and any search can be saved as
// a command that re-runs on every daily auto-sync.
export default function LeadChatPanel({
  campaignId,
  campaignName,
  onApplyFilter,
  onClearFilter,
  activeFilterLabel,
}) {
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [commands, setCommands] = useState([]);
  const [commandsOpen, setCommandsOpen] = useState(false);
  // Which assistant message has its save-as-command form open, plus the form.
  const [savingFor, setSavingFor] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [saveDaily, setSaveDaily] = useState(true);
  const [saveBusy, setSaveBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => subscribeLeadCommands(setCommands), []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const dailyCommandCount = useMemo(
    () => commands.filter((c) => c.runOnDailySync).length,
    [commands]
  );

  const ask = async (text) => {
    const question = String(text || "").trim();
    if (!question || loading) return;
    setInput("");
    setSavingFor(null);
    const history = messages
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setLoading(true);
    try {
      const r = await leadChat(question, { campaignId, history });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: r.answer,
          leadIds: r.leadIds || [],
          matchedCount: r.matchedCount || 0,
          scannedCount: r.scannedCount || 0,
          suggestedCommandName: r.suggestedCommandName || "",
          prompt: question,
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: e.message, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const applyMatches = (m) => {
    if (!m.leadIds?.length) return;
    onApplyFilter?.(m.leadIds, m.suggestedCommandName || m.prompt);
  };

  const openSaveForm = (idx, m) => {
    setSavingFor(idx);
    setSaveName(m.suggestedCommandName || m.prompt?.slice(0, 40) || "");
    setSaveDaily(true);
  };

  const saveCommand = async (m) => {
    const name = saveName.trim();
    if (!name) return;
    setSaveBusy(true);
    try {
      await createLeadCommand({
        name,
        prompt: m.prompt,
        campaignId: campaignId || null,
        runOnDailySync: saveDaily,
        lastMatchCount: m.matchedCount,
        lastMatchLeadIds: (m.leadIds || []).slice(0, 30),
      });
      setSavingFor(null);
      push({
        title: "Command saved",
        desc: saveDaily
          ? "It'll run on every daily auto-sync and show up in your email digest."
          : "Saved — run it anytime from this panel.",
        variant: "success",
      });
    } catch (e) {
      push({ title: "Save failed", desc: e.message, variant: "error" });
    } finally {
      setSaveBusy(false);
    }
  };

  const toggleDaily = async (cmd) => {
    try {
      await updateLeadCommand(cmd.id, { runOnDailySync: !cmd.runOnDailySync });
    } catch (e) {
      push({ title: "Update failed", desc: e.message, variant: "error" });
    }
  };

  const removeCommand = async (cmd) => {
    try {
      await deleteLeadCommand(cmd.id);
    } catch (e) {
      push({ title: "Delete failed", desc: e.message, variant: "error" });
    }
  };

  const applyCommandResults = (cmd) => {
    if (!cmd.lastMatchLeadIds?.length) {
      push({
        title: "No stored matches",
        desc: "Run the command to search your current leads.",
        variant: "error",
      });
      return;
    }
    onApplyFilter?.(cmd.lastMatchLeadIds, cmd.name);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open lead assistant"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#0B1038] text-white text-sm font-semibold shadow-lg hover:bg-[#151c54] transition-colors"
      >
        <Sparkles className="w-4 h-4 text-blue-300" />
        Ask your leads
      </button>
    );
  }

  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-40 flex flex-col bg-white sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] sm:rounded-md sm:border sm:border-gray-200 sm:shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-[#0B1038] text-white sm:rounded-t-md">
        <Bot className="w-4 h-4 text-blue-300" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">
            Lead assistant
          </div>
          <div className="text-[11px] text-blue-200 truncate">
            {campaignId
              ? `Searching: ${campaignName || "current campaign"}`
              : "Searching: all campaigns"}{" "}
            · {COLLAVUE_COSTS.lead_chat} credit per question
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close lead assistant"
          className="p-1.5 rounded hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Saved commands */}
      {commands.length > 0 && (
        <div className="border-b border-gray-100 bg-gray-50">
          <button
            onClick={() => setCommandsOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-600 hover:text-[#0B1038]"
          >
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              Saved commands ({commands.length}) · {dailyCommandCount} run
              daily
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${
                commandsOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {commandsOpen && (
            <ul className="px-3 pb-2 space-y-1.5 max-h-40 overflow-y-auto">
              {commands.map((c) => (
                <li
                  key={c.id}
                  className="bg-white border border-gray-200 rounded-sm px-2.5 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-xs font-semibold text-[#0B1038] truncate"
                      title={c.prompt}
                    >
                      “{c.name}”
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {Number.isFinite(c.lastMatchCount)
                        ? `${c.lastMatchCount} match${
                            c.lastMatchCount === 1 ? "" : "es"
                          } last run`
                        : "not run yet"}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                    <button
                      onClick={() => applyCommandResults(c)}
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline font-semibold"
                    >
                      <Filter className="w-3 h-3" /> Show matches
                    </button>
                    <button
                      onClick={() => ask(c.prompt)}
                      disabled={loading}
                      className="inline-flex items-center gap-1 text-gray-600 hover:text-[#0B1038] font-semibold disabled:opacity-50"
                    >
                      <Play className="w-3 h-3" /> Run now (
                      {COLLAVUE_COSTS.lead_chat} cr)
                    </button>
                    <label className="ml-auto inline-flex items-center gap-1 cursor-pointer text-gray-600">
                      <input
                        type="checkbox"
                        checked={!!c.runOnDailySync}
                        onChange={() => toggleDaily(c)}
                        className="w-3 h-3 accent-blue-600"
                      />
                      Daily
                    </label>
                    <button
                      onClick={() => removeCommand(c)}
                      aria-label={`Delete command ${c.name}`}
                      className="text-gray-400 hover:text-rose-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Active AI filter chip */}
      {activeFilterLabel && (
        <div className="px-4 py-2 border-b border-blue-100 bg-blue-50 flex items-center justify-between gap-2">
          <span className="text-xs text-blue-800 truncate">
            Inbox filtered: <strong>{activeFilterLabel}</strong>
          </span>
          <button
            onClick={onClearFilter}
            className="text-xs font-semibold text-blue-700 hover:underline shrink-0"
          >
            Clear
          </button>
        </div>
      )}

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center pt-6">
            <Bot className="w-8 h-8 text-blue-500 mx-auto" />
            <p className="mt-2 text-sm text-gray-700 font-medium">
              Talk to your leads in plain English.
            </p>
            <p className="mt-1 text-xs text-gray-500 px-4">
              I read {campaignId ? "this campaign's" : "your"} leads and can
              filter, summarize, or answer questions about them. Save any
              search to re-run automatically on your daily sync.
            </p>
            <div className="mt-4 space-y-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => ask(ex)}
                  className="block w-full text-left px-3 py-2 rounded-sm border border-gray-200 bg-gray-50 text-xs text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  “{ex}”
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, idx) =>
          m.role === "user" ? (
            <div key={idx} className="flex justify-end">
              <div className="max-w-[85%] px-3 py-2 rounded-md bg-[#0B1038] text-white text-sm whitespace-pre-wrap">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={idx} className="flex justify-start">
              <div
                className={`max-w-[90%] px-3 py-2 rounded-md border text-sm whitespace-pre-wrap ${
                  m.error
                    ? "bg-rose-50 border-rose-200 text-rose-700"
                    : "bg-gray-50 border-gray-200 text-gray-800"
                }`}
              >
                {m.content}
                {!m.error && m.matchedCount > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="text-[11px] text-gray-500 mb-1.5">
                      {m.matchedCount} of {m.scannedCount} leads matched
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => applyMatches(m)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        <Filter className="w-3 h-3" /> Show in inbox
                      </button>
                      <button
                        onClick={() => openSaveForm(idx, m)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <CalendarClock className="w-3 h-3" /> Save as command
                      </button>
                    </div>
                    {savingFor === idx && (
                      <div className="mt-2 p-2 bg-white border border-gray-200 rounded-sm">
                        <input
                          value={saveName}
                          onChange={(e) => setSaveName(e.target.value)}
                          placeholder="Command name"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-sm text-xs outline-none focus:border-blue-600"
                        />
                        <label className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveDaily}
                            onChange={(e) => setSaveDaily(e.target.checked)}
                            className="w-3 h-3 accent-blue-600"
                          />
                          Run on every daily auto-sync (
                          {COLLAVUE_COSTS.lead_command_run} credit per run,
                          results in your email digest)
                        </label>
                        {saveDaily &&
                          dailyCommandCount >= COLLAVUE_MAX_DAILY_COMMANDS && (
                            <p className="mt-1 text-[10px] text-amber-600">
                              You already have {COLLAVUE_MAX_DAILY_COMMANDS}{" "}
                              daily commands — only the first{" "}
                              {COLLAVUE_MAX_DAILY_COMMANDS} run per sync.
                            </p>
                          )}
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => saveCommand(m)}
                            disabled={saveBusy || !saveName.trim()}
                            className="px-2.5 py-1 rounded-sm bg-[#0B1038] text-white text-[11px] font-semibold hover:bg-[#151c54] disabled:opacity-50"
                          >
                            {saveBusy ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={() => setSavingFor(null)}
                            className="px-2.5 py-1 rounded-sm border border-gray-300 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-500 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Reading your leads…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(input);
        }}
        className="border-t border-gray-200 p-3 flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Try "find every lead that mentions…"'
          maxLength={600}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Send"
          className="p-2.5 rounded-sm bg-[#0B1038] text-white hover:bg-[#151c54] disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
